<div class="h-full">
  <div class="w-full relative">
    @if (isLoading()) {
      <div class="w-full absolute">
        <mat-progress-bar class="mds-x-small" [mode]="'indeterminate'"></mat-progress-bar>
      </div>
    }
  </div>

  <div
    class="h-full flex flex-1 flex-col p-4 bg-white border border-gray-0 rounded overflow-y-scroll"
  >
    <!-- error alert -->
    @if (hasError()) {
      <div class="w-full mb-4">
        <modus-alert class="w-full" message="{{ errorMessage() }}" type="error"></modus-alert>
      </div>
    }
    <!-- default role assignment -->
    <div>
      <div class="font-semibold">{{ translations.defaultRole.title.text }}</div>
      <div>{{ translations.defaultRole.adminRoleNote.text }}</div>
      <div class="mt-2">
        <mat-radio-group [formControl]="defaultRoleControl">
          @for (roleOption of defaultRoleOptions; track roleOption.role) {
            <mat-radio-button color="primary" class="mr-4" [value]="roleOption.role">
              {{ roleOption.displayName }}
            </mat-radio-button>
          }
        </mat-radio-group>
      </div>
    </div>

    <!-- groups list -->
    @if (enableGroupRoleFeature()) {
      <div class="w-full mt-6">
        <div class="font-semibold">{{ translations.groups.title.text }}</div>
        <div class="w-full mt-2 border-t border-gray-0">
          @if (hasGroups()) {
            @for (groupRole of groupRoleAssignments(); track groupRole.id) {
              <div class="flex flex-row items-center p-2 pl-0 border-b border-gray-0">
                <div class="flex-1">
                  {{ groupRole.name }}
                  <span class="ml-2 text-sm text-gray-6">({{ groupRole.userLabel }})</span>
                </div>
                <mat-select
                  disableRipple
                  id="user-role"
                  class="modus-dropdown !flex !w-40 min-w-fit !px-2 h-8 hover:bg-gray-0"
                  panelClass="modus-dropdown-panel"
                  [formControl]="groupRole.formGroup.controls.role"
                  [modus-tooltip]="translations.groups.role.tooltip.text"
                  [ngClass]="{
                    'hover:bg-gray-0': !isDisabled(),
                    'opacity-70': isDisabled(),
                  }"
                >
                  @for (roleOption of roleOptions; track roleOption.role) {
                    <mat-option [value]="roleOption.role">
                      <span class="whitespace-nowrap">{{ roleOption.displayName }}</span>
                    </mat-option>
                  }
                </mat-select>
              </div>
            }
          } @else if (!isLoading()) {
            <div class="italic p-2 pl-0">
              {{ translations.groups.noGroups.text }}
            </div>
          }
        </div>
      </div>
    }

    <!-- user role list -->
    @if (enableUserRoleFeature()) {
      <!-- add user -->
      <div class="max-w-sm mt-6">
        <div class="font-semibold">{{ translations.users.title.text }}</div>

        <modus-autocomplete-input
          #addUserAutocomplete
          data-cy="scanner-type-autocomplete"
          [clearable]="true"
          [options]="addUserOptionList"
          [formControl]="addUserControl"
          [placeholder]="translations.users.add.placeholder.text"
          (optionSelected)="addUser($event)"
        >
        </modus-autocomplete-input>
        <modus-option-list
          #addUserOptionList="modusOptionList"
          [noResultsFoundText]="translations.optionList.noResultsFound.text"
          [noResultsFoundSubtext]="translations.optionList.noResultsFoundHint.text"
        >
          @for (userOption of addMemberOptions(); track userOption.id) {
            <modus-option [value]="userOption.id">
              <div class="overflow-hidden text-ellipsis whitespace-nowrap">
                {{ userOption.memberDisplayValue }}
              </div>
            </modus-option>
          }
        </modus-option-list>
      </div>

      <!-- users list -->
      <div class="w-full mt-2 border-t border-gray-0">
        @if (hasUsers()) {
          @for (userRole of userRoleAssignments(); track userRole.tidUuid) {
            <div class="flex flex-row items-center p-2 pl-0 border-b border-gray-0">
              <div class="flex-1">
                {{ userRole.displayValue }}
                @if (userRole.userIsRemoved) {
                  -
                  <span class="text-sm text-red">{{ translations.users.status.removed.text }}</span>
                }
              </div>

              <mat-select
                disableRipple
                id="user-role"
                class="modus-dropdown !flex !w-40 min-w-fit !px-2 h-8 hover:bg-gray-0"
                panelClass="modus-dropdown-panel"
                [formControl]="userRole.formGroup.controls.role"
                [modus-tooltip]="translations.users.role.tooltip.text"
                [ngClass]="{
                  'hover:bg-gray-0': !userRole.formGroup.controls.role.disabled,
                  'opacity-70': userRole.formGroup.controls.role.disabled,
                }"
              >
                @for (roleOption of roleOptions; track roleOption.role) {
                  <mat-option [value]="roleOption.role">
                    <span class="whitespace-nowrap">{{ roleOption.displayName }}</span>
                  </mat-option>
                }
              </mat-select>

              <button
                modus-icon-button
                id="remove-user"
                class="text-gray-8 ml-2"
                [modus-tooltip]="translations.users.remove.tooltip.text"
                [disabled]="isDisabled()"
                (click)="removeUser(userRole.tidUuid)"
              >
                <md-icon>close</md-icon>
              </button>
            </div>
          }
        }
      </div>
    }

    <!-- member with effective role list -->
    <div class="w-full mt-6">
      <div class="font-semibold">{{ translations.effectiveMemberRoles.title.text }}</div>
      <div class="w-full mt-2 border-t border-gray-0">
        @for (member of members(); track member.id) {
          <div class="flex flex-col p-2 pl-0 border-b border-gray-0">
            <span>
              {{ member.memberDisplayValue }}
            </span>
            <span class="text-sm text-gray-6">
              {{ member.roleDisplayName }}
              @if (member.isRemoved) {
                - <span class="text-red">{{ translations.users.status.removed.text }}</span>
              }
            </span>
          </div>
        }
      </div>
    </div>
  </div>
</div>
